import { ThemeUICSSObject } from 'theme-ui';
import { responsive, smallButton, spacing } from '../util';
import getColorsAndStatesByPath from './colors/util/getColorsAndStatesByPath';

const buttons: Record<string, ThemeUICSSObject> = {
  inherit: {
    color: 'inherit',
    background: 'inherit',
    border: 'none',
    borderRadius: 0,
    font: 'inherit',
    cursor: 'pointer',
    padding: 0,
    margin: 0,
  },
  'inherit-sm': {
    variant: 'buttons.inherit',
    ...smallButton,
  },
  collapsibleAsidePanelTitle: {
    variant: 'buttons.inherit',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    ...getColorsAndStatesByPath('buttons.collapsibleAsidePanelTitle'),
  },
  primary: {
    borderWidth: '2px',
    borderStyle: 'solid',
    variant: 'text.default',
    py: responsive({ 0: 3, 4: 4 }),
    px: responsive({ 0: 5, 4: 6 }),
    display: 'inline-block',
    width: responsive({ 0: '100%', 1: 'auto' }),
    cursor: 'pointer',
    borderRadius: 'buttons',
    fontWeight: 'normal',
    userSelect: 'none',
    transition: 'background-color 300ms ease-out, color 300ms ease-out',
    wordBreak: 'keep-all',

    ...getColorsAndStatesByPath('buttons.primary'),

    '&:focus, &:focus-visible': {
      outlineColor: '#00daff',
    },
  },
  'primary-sm': {
    variant: 'buttons.primary',
    '&:focus, &:focus-visible': {},
    ...smallButton,
  },
  secondary: {
    variant: 'buttons.primary',
    '&:focus, &:focus-visible': {},
    borderWidth: '0px',
    borderStyle: 'solid',

    ...getColorsAndStatesByPath('buttons.secondary'),
  },
  'secondary-sm': {
    variant: 'buttons.secondary',
    ...smallButton,
  },
  'light-secondary': {
    variant: 'buttons.secondary',

    ...getColorsAndStatesByPath('buttons.lightSecondary'),
  },
  danger: {
    variant: 'buttons.primary',
    '&:focus, &:focus-visible': {},

    ...getColorsAndStatesByPath('buttons.danger'),
  },
  'danger-sm': {
    variant: 'buttons.danger',
    ...smallButton,
  },
  warning: {
    variant: 'buttons.primary',
    '&:focus, &:focus-visible': {},

    ...getColorsAndStatesByPath('buttons.warning'),
  },
  'warning-sm': {
    variant: 'buttons.warning',
    ...smallButton,
  },
  close: {
    cursor: 'pointer',
    ml: 'auto',
    mr: '2',
    borderRadius: 'buttons',

    ...getColorsAndStatesByPath('buttons.close'),
  },
  accordion: {
    variant: 'buttons.primary',
    '&:focus, &:focus-visible': {},
    cursor: 'pointer',
    m: spacing(0),
    py: responsive({ 0: 3, 4: 4 }),
    px: responsive({ 0: 5, 4: 6 }),
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: 'buttons',
    textAlign: 'left',
    fontFamily: 'heading',
    fontSize: 'text.default',
    textDecoration: 'none',
    textTransform: 'none',
    fontWeight: 'bold',
    border: 'none',
    transition: 'background-color 300ms ease-out, color 300ms ease-out',
    userSelect: 'text',

    '&, *': getColorsAndStatesByPath('buttons.accordion'),

    'h2.toggleAccordionElementLabel': {
      variant: 'text.default',

      color: 'inherit',
      margin: 0,
      wordBreak: 'break-word',
    },
  },
  'accordion-primary': {
    variant: 'buttons.accordion',
    border: 'none',

    ...getColorsAndStatesByPath('buttons.accordionPrimary'),
  },
  icon: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    p: spacing(0),
    m: spacing(0),
    bg: 'secondary',
    cursor: 'pointer',
    transition: 'background-color 300ms ease-out, color 300ms ease-out',
    borderRadius: 'buttons',
    border: 'none',
    color: 'inherit',
    ...getColorsAndStatesByPath('buttons.icon'),

    '&.isToggled': {
      ...getColorsAndStatesByPath('buttons.iconToggled'),
    },
  },
  'icon-primary': {
    variant: 'buttons.icon',
    ...getColorsAndStatesByPath('buttons.iconPrimary'),
  },
  'icon-outline': {
    variant: 'buttons.icon',
    bg: 'white',
    border: '1px solid',
    borderRadius: '4px',
    // border: 'none',

    ...getColorsAndStatesByPath('buttons.iconOutline'),
  },
  'icon-outline-danger': {
    variant: 'buttons.icon',
    border: '1px solid',

    ...getColorsAndStatesByPath('buttons.outlineDanger'),
  },
  'icon-only': {
    variant: 'buttons.icon',

    ...getColorsAndStatesByPath('buttons.iconOnly'),
  },
  link: {
    variant: 'inherit',
    width: 'auto',
    border: 'none',
    background: 'transparent',
    textDecoration: 'underline',
    px: 2,
    py: 1,
    borderRadius: 'buttons',
    cursor: 'pointer',
    textTransform: 'none',
    transition: 'background-color 300ms ease-out, color 300ms ease-out',

    ...getColorsAndStatesByPath('buttons.link'),
  },
  'link-sm': {
    variant: 'buttons.link',
    ...smallButton,
  },
  outline: {
    variant: 'buttons.primary',
    borderWidth: '2px',
    borderStyle: 'solid',
    borderRadius: 'buttons',

    '&:focus, &:focus-visible': {},
    ...getColorsAndStatesByPath('buttons.outline', {
      states: {
        active: false,
        checked: false,
        disabled: false,
        focus: false,
        hover: false,
        readonly: false,
        selected: false,
      },
    }),
  },
  'outline-sm': {
    variant: 'buttons.outline',
    ...smallButton,
  },
  'transparent-sm': {
    variant: 'buttons.outline',
    '&:focus, &:focus-visible': {},
    bg: 'transparent',
    color: 'inherit',
    border: 'none',
    font: 'inherit',
    textTransform: 'inherit',
    ...smallButton,
  },
  'outline-danger': {
    variant: 'buttons.outline',
    '&:focus, &:focus-visible': {},

    ...getColorsAndStatesByPath('buttons.outlineDanger'),
  },
  'outline-danger-sm': {
    variant: 'buttons.outline-danger',
    ...smallButton,
  },
  'outline-warning': {
    variant: 'buttons.outline',
    '&:focus, &:focus-visible': {},

    ...getColorsAndStatesByPath('buttons.outlineWarning'),
  },
  'outline-warning-sm': {
    variant: 'buttons.outline-warning',
    ...smallButton,
  },
  'outline-extended': {
    variant: 'buttons.outline',
    gridColumnStart: 1,
    gridColumnEnd: 3,
    width: '100%',
  },
  'outline-danger-extended': {
    variant: 'buttons.outline-danger',
    gridColumnStart: 1,
    gridColumnEnd: 3,
    width: '100%',
  },
  'primary-extended': {
    variant: 'buttons.primary',
    '&:focus, &:focus-visible': {},
    gridColumnStart: 1,
    gridColumnEnd: 3,
    width: '100%',
  },
  query: {
    extended: {
      variant: 'buttons.outline',
      '&:focus, &:focus-visible': {},
      gridColumnStart: 1,
      gridColumnEnd: 3,
    },
  },
  tableAccordion: {
    variant: 'buttons.accordion',
    fontSize: 'text.default',
    p: spacing(3),
    wordBreak: 'keep-all',
    a: {
      color: 'background',
    },

    ...getColorsAndStatesByPath('buttons.tableAccordion'),
  },
  tableHeader: {
    variant: 'buttons.tableAccordion',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    border: 'none',
    svg: {
      ml: spacing(2),
    },
    ...getColorsAndStatesByPath('buttons.tableHeader'),
  },
  paper: {
    variant: 'buttons.primary',
    borderWidth: '2px',
    borderStyle: 'solid',
    borderRadius: 'buttons',

    ...getColorsAndStatesByPath('buttons.paper'),
  },
  'paper-sm': {
    variant: 'buttons.primary',
    borderWidth: '2px',
    borderStyle: 'solid',
    borderRadius: 'buttons',

    ...getColorsAndStatesByPath('buttons.paper'),
    ...smallButton,
  },
};

export default buttons;
